import { useEffect, useRef, useState } from 'react';
import { addDays, format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import './Calendar.css'; 
import DayItem from './DayItem';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { ru } from 'date-fns/locale';
import useScreenWidth from '../../hooks/useScreenWidth'; 

interface CalendarProps {
    onDateSelect: (date: Date) => void;
    startDate: Date | null;
    endDate: Date | null;
}

const monthNamesNominative = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const CustomCalendar: React.FC<CalendarProps> = ({ onDateSelect, startDate, endDate }) => {
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [days, setDays] = useState<Date[]>(generateDays(new Date(), false));
    const calendarRef = useRef<HTMLDivElement>(null);
    const [dayWidth, setDayWidth] = useState<number>(0);
    const screenWidth = useScreenWidth(); 
    
    function generateDays(date: Date, fullMonth: boolean): Date[] {
        const daysArray: Date[] = [];
        if (fullMonth) {
            const start = startOfMonth(date);
            const end = endOfMonth(date);
            let currentDay = start;
            while (currentDay <= end) {
                daysArray.push(currentDay);
                currentDay = addDays(currentDay, 1);
            }
        } else {
            for (let i = -10; i <= 10; i++) {
                daysArray.push(addDays(date, i));
            }
        }
        return daysArray;
    }
    
    const handlePrevClick = () => {
        setCurrentDate(prevDate => {
            const newStartDate = subDays(prevDate, screenWidth > 1000 ? 7 : 31);
            setDays(generateDays(newStartDate, screenWidth <= 1000));
            return newStartDate;
        });
    };
    
    const handleNextClick = () => {
        setCurrentDate(prevDate => {
            const newStartDate = addDays(prevDate, screenWidth > 1000 ? 7 : 31);
            setDays(generateDays(newStartDate, screenWidth <= 1000));
            return newStartDate;
        });
    };
    
    const getVisibleMonths = (): string[] => {
        const visibleMonths: string[] = [];
        const firstVisibleDay = days[0];
        const lastVisibleDay = days[days.length - 1];
        const firstMonthIndex = parseInt(format(firstVisibleDay, 'M', { locale: ru })) - 1;  
        const lastMonthIndex = parseInt(format(lastVisibleDay, 'M', { locale: ru })) - 1;  
        const firstMonth = monthNamesNominative[firstMonthIndex];
        const lastMonth = monthNamesNominative[lastMonthIndex];
        
        if (firstMonth !== lastMonth) {
            visibleMonths.push(firstMonth);
            visibleMonths.push(lastMonth);
        } else {
            visibleMonths.push(firstMonth);
        }
        
        return visibleMonths;
    };
    
    const handleDateClick = (day: Date) => {
        onDateSelect(day); 
    };
    
    useEffect(() => {
        const updateDayWidth = () => {
            if (calendarRef.current) {
                const calendarWidth = calendarRef.current.clientWidth;
                const newDayWidth = (calendarWidth - 35) / (screenWidth <= 1000 ? 30 : 21);
                setDayWidth(newDayWidth);
            }
        };
        
        setDays(generateDays(currentDate, screenWidth <= 1000));
        updateDayWidth();
        window.addEventListener('resize', updateDayWidth);
        return () => window.removeEventListener('resize', updateDayWidth);
    }, [screenWidth, currentDate]);
    
    return (
        <div className="calendar" ref={calendarRef}>
            <div className="months">
                {getVisibleMonths().map((month, index) => (
                    <span key={index} className="month">{month}</span>
                ))}
            </div>
            <div className="calendar_header">
                <div className="arrow" onClick={handlePrevClick}><ChevronLeft size={20} /></div>
                <div className="days_container">
                    {days.map((day, index) => (
                        <DayItem
                            key={index}
                            day={day}
                            startDate={startDate}
                            endDate={endDate}
                            onClick={handleDateClick}
                            width={dayWidth}
                        />
                    ))}
                </div>
                <div className="arrow" onClick={handleNextClick}><ChevronRight size={20} /></div>
            </div>
        </div>
    );
};

export default CustomCalendar;